import { onPage } from '../../app/lifecycle'
import { getState, on, setState, storage, subscribe } from '../../app/store'
import { createSounds } from './audio'
import { fetchBoard, formatScore, renderBoard, renderMessage, submitScore } from './highscore'
import { gridFor, layoutFor, placeWord } from './layout'
import { createRenderer } from './renderer'
import {
  advance,
  createRound,
  hasEffect,
  intervalOf,
  pineappleFading,
  pineappleShown,
  rewrap,
  snakeLook,
  step,
  turn,
} from './round'
import { rasterizeWord } from './wordmark'

/**
 * Snake on the home page (block «snake», template-parts/blocks/block-snake.php).
 *
 * Idle, the word ("danino") sits in the middle of the screen in squares. The
 * footer "play", an arrow/WASD key or a tap starts a round: the squares of the
 * word become food worth a quarter point each, and a pizza ingredient worth a
 * point is always somewhere on the screen — now and then with a pineapple
 * next to it, and a chili among them (rules in round.js). Score and the
 * global highscore show in the footer bar; on death a dialog offers the
 * highscore list.
 *
 * Touch steers with a swipe, or with a tap beside the head: above or below it
 * while the snake runs sideways, left or right of it while it runs up or down
 * (along an edge, a tap across the screen turns out through that edge).
 * Every finger counts on its own, so two thumbs can tap in turn.
 *
 * The frame loop is the round's clock: it only runs while playing, so time
 * spent paused, hidden or behind the nav never counts — neither for the
 * effects and the pineapple nor as play time for the highscore.
 *
 *   loading → idle → playing ⇄ paused
 *                      ↓
 *                     over (dialog) → playing ("nochmal") | idle (close)
 *
 * The easy view ("off") has no game: the block keeps its text version and
 * only the top list is filled in.
 */
const KEY_DIRECTIONS = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  KeyW: 'up',
  KeyS: 'down',
  KeyA: 'left',
  KeyD: 'right',
}
const PAUSE_KEYS = new Set(['Space', 'KeyP'])
const NAME_STORAGE_KEY = 'danino:snake-name'

const SWIPE_PX = 24
const TAP_PX = 10
// Longer is a press, not a tap — a finger resting on the screen or held down
// never steers when it lifts (the platforms' long-press delay).
const TAP_MAX_MS = 500
// Nearer than this to the edge of the area a tap can reach, there is no room
// left to tap beyond the head (see tapSide()).
const TAP_EDGE_CELLS = 2
// The tap feedback square spans this many cells (40_game.sass, .snake-tap).
const TAP_MARK_CELLS = 3
const TAP_MARK_MS = 400
// Space kept between a revealed element and the edge of the dialog body.
const REVEAL_MARGIN_PX = 12
const MAX_FRAME_MS = 250
const MAX_STEPS_PER_FRAME = 3
const DEATH_BLINKS = 6
const DEATH_BLINK_MS = 110

// Read on every frame of a round, so the query is created once.
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
const prefersReducedMotion = () => reducedMotion.matches

/**
 * The mobile nav is a full-screen overlay that makes the page inert: no round
 * may start, resume or run behind it.
 */
const navOpen = () => getState().navOpen

const isEditable = (target) =>
  target instanceof Element &&
  Boolean(
    target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')
  )

function readStrings(block) {
  try {
    return JSON.parse(block.dataset.snakeStrings || '{}')
  } catch {
    return {}
  }
}

/** Score and highscore in the footer bar (outside the Swup container). */
function createHud() {
  const score = document.querySelector('[data-game-score]')
  const scoreValue = document.querySelector('[data-game-score-value]')
  const best = document.querySelector('[data-game-highscore]')
  const bestValue = document.querySelector('[data-game-highscore-value]')

  return {
    /** @param {number|null} record  global best; the HUD stays hidden until it is known */
    update(points, record) {
      if (score) score.hidden = false
      if (scoreValue) scoreValue.textContent = formatScore(points)
      if (best) best.hidden = record === null
      // A round that beats the list shows as the new highscore right away.
      if (bestValue && record !== null) {
        bestValue.textContent = formatScore(Math.max(record, points))
      }
    },
    hide() {
      if (score) score.hidden = true
      if (best) best.hidden = true
      if (scoreValue) scoreValue.textContent = '0'
    },
  }
}

function mountGame(block) {
  const word = block.dataset.snakeWord || 'danino'
  const listUrl = block.dataset.snakeListUrl
  const submitUrl = block.dataset.snakeSubmitUrl
  const strings = readStrings(block)

  const safeArea = block.querySelector('[data-snake-safe-area]')
  const pauseLabel = block.querySelector('[data-snake-pause]')
  const dialog = block.querySelector('[data-snake-dialog]')
  const dialogBody = dialog?.querySelector('[data-snake-dialog-body]')
  const form = dialog?.querySelector('[data-snake-form]')
  const nameInput = dialog?.querySelector('[data-snake-name]')
  const submitButton = dialog?.querySelector('[data-snake-submit]')
  const statusLine = dialog?.querySelector('[data-snake-status]')
  const boardList = dialog?.querySelector('[data-snake-board-list]')
  const finalScore = dialog?.querySelector('[data-snake-final-score]')
  const againButton = dialog?.querySelector('[data-snake-again]')
  const closeButton = dialog?.querySelector('[data-snake-close]')

  // Every listener goes through listen(), so aborting removes them all.
  const controller = new AbortController()
  const { signal } = controller
  const listen = (target, type, handler, options = {}) =>
    target?.addEventListener(type, handler, { ...options, signal })

  const hud = createHud()
  const sounds = createSounds()
  const renderer = createRenderer(block)

  let mode = 'loading'
  let destroyed = false
  let pendingStart = null
  let shape = { cols: 0, rows: 0, cells: [] }
  let grid = null
  let wordCells = []
  let round = null
  let result = null
  let record = null
  let recordRequested = false
  let submitting = false
  let submitted = false
  let dialogWaiting = false
  let reopenTimer = 0
  let frame = 0
  let resizeFrame = 0
  let deathTimer = 0
  let lastTime = 0
  let backlog = 0
  // Touch and pen pointers on the game, by pointerId: each one is a tap or a
  // swipe of its own, so two thumbs can steer in turn like two key presses.
  const touches = new Map()

  const setStatus = (text = '') => {
    if (statusLine) statusLine.textContent = text
  }

  /**
   * With a name to enter, the form shows and "eintragen" is the main action;
   * without (no points, or entered already) "nochmal" takes over.
   */
  const setEntering = (entering) => {
    if (form) form.hidden = !entering
    againButton?.classList.toggle('is-primary', !entering)
  }

  /**
   * The box that scrolls an element of the dialog: the body — or, on a phone
   * held sideways, its column (40_game.sass).
   */
  function scrollerOf(element) {
    for (let box = element.parentElement; box && box !== dialog; box = box.parentElement) {
      if (/auto|scroll/.test(getComputedStyle(box).overflowY)) return box
    }
    return null
  }

  /**
   * Scroll just far enough to show the elements in the scrolling box of the
   * first one; others (on a phone held sideways, in the other column) are
   * left alone. Where they don't all fit, the first one shows in full and the
   * others as far as they fit next to it. By hand, as scrollIntoView() could
   * also move the page behind the modal.
   */
  function reveal(elements, behavior = 'auto') {
    const shown = elements.filter((element) => element?.getClientRects().length)
    const scroller = shown.length ? scrollerOf(shown[0]) : null
    if (!scroller) return

    const rects = shown
      .filter((element) => scroller.contains(element))
      .map((element) => element.getBoundingClientRect())
    const box = scroller.getBoundingClientRect()
    const room = box.height - 2 * REVEAL_MARGIN_PX
    let top = Math.min(...rects.map((rect) => rect.top))
    let bottom = Math.max(...rects.map((rect) => rect.bottom))
    if (bottom - top > room) {
      const [first] = rects
      if (top < first.top) {
        // The others are above it: the first at the foot, as much of them as fits.
        bottom = first.bottom
        top = Math.min(first.top, bottom - room)
      } else {
        top = first.top
        bottom = Math.max(first.bottom, top + room)
      }
    }

    const above = top - box.top - REVEAL_MARGIN_PX
    const below = bottom - box.bottom + REVEAL_MARGIN_PX
    const delta = above < 0 ? above : Math.min(Math.max(below, 0), above)
    if (delta) scroller.scrollBy({ top: delta, behavior })
  }

  /** The name field, its button and the line below them that answers a submission. */
  const revealField = () => reveal([nameInput, submitButton, statusLine])

  /** A submission failed: the message first — the keyboard may still cover half the sheet. */
  const revealProblem = () => reveal([statusLine, nameInput, submitButton])

  /**
   * Phones: the dialog is a sheet over the visual viewport — the part of the
   * screen above the on-screen keyboard — so the name field and the buttons
   * stay in reach (40_game.sass; without the two properties it covers 100dvh).
   * Not while zoomed in, or the sheet would shrink to the zoomed part.
   */
  function fitDialog() {
    const viewport = window.visualViewport
    if (!dialog?.open || !viewport) return
    if (Math.abs(viewport.scale - 1) < 0.01) {
      dialog.style.setProperty('--snake-dialog-top', `${viewport.offsetTop}px`)
      dialog.style.setProperty('--snake-dialog-height', `${viewport.height}px`)
    } else {
      dialog.style.removeProperty('--snake-dialog-top')
      dialog.style.removeProperty('--snake-dialog-height')
    }
    // The keyboard has come up (or gone): keep the field in sight.
    if (document.activeElement === nameInput) revealField()
  }

  /** The canvas box is the viewport; the safe area is the part between the bars. */
  function measure() {
    const box = renderer.canvas.getBoundingClientRect()
    const safe = safeArea?.getBoundingClientRect()
    return {
      width: box.width,
      height: box.height,
      safeTop: safe ? Math.max(0, safe.top - box.top) : 0,
      safeBottom: safe ? Math.max(0, box.bottom - safe.bottom) : 0,
    }
  }

  function draw({ hideSnake = false } = {}) {
    if (!round) {
      renderer.draw(grid, { squares: [wordCells] })
      return
    }

    const still = prefersReducedMotion()
    const ingredients = round.ingredient ? [round.ingredient] : []
    // In its last seconds the pineapple blinks while the round runs. With
    // reduced motion it fades instead — and so it does on a paused or finished
    // board: a frozen frame must not catch it blinked out, or a player looking
    // at the board sees nothing where it still waits.
    const blinking = mode === 'playing' && !still
    if (round.pineapple && (!blinking || pineappleShown(round))) {
      ingredients.push({ ...round.pineapple, dim: !blinking && pineappleFading(round) })
    }

    renderer.draw(grid, {
      squares: [round.word.values()],
      snake: hideSnake
        ? null
        : { cells: round.body, look: snakeLook(round), time: round.time, still },
      ingredients,
    })
  }

  /**
   * Without a round everything is laid out afresh (cell size, word position).
   * During one the cells keep their size and positions wrap onto the new grid.
   */
  function relayout() {
    const view = measure()
    if (!view.width || !view.height) return
    renderer.resize(view.width, view.height)

    if (round && grid) {
      grid = gridFor(view, grid.cell)
      rewrap(round, grid)
    } else {
      grid = layoutFor(view, shape)
      wordCells = placeWord(grid, shape)
    }
    draw()
  }

  function scheduleRelayout() {
    cancelAnimationFrame(resizeFrame)
    resizeFrame = requestAnimationFrame(() => {
      if (mode !== 'loading') relayout()
    })
  }

  function loadRecord() {
    if (recordRequested || !listUrl) return
    recordRequested = true
    fetchBoard(listUrl, signal)
      .then(({ best }) => {
        record = best?.score ?? 0
        if (mode !== 'idle') hud.update(round?.score ?? 0, record)
      })
      .catch(() => {
        recordRequested = false
      })
  }

  function run() {
    cancelAnimationFrame(frame)
    lastTime = performance.now()
    frame = requestAnimationFrame(tick)
  }

  function tick(now) {
    frame = requestAnimationFrame(tick)
    const delta = Math.min(Math.max(0, now - lastTime), MAX_FRAME_MS)
    lastTime = now
    backlog += delta
    // The pineapple may run out and an effect end without a step.
    const changed = advance(round, delta)

    let steps = 0
    while (steps < MAX_STEPS_PER_FRAME && backlog >= intervalOf(round)) {
      backlog -= intervalOf(round)
      steps += 1

      const { died, food, effect } = step(round)
      if (died) {
        die()
        return
      }
      if (food) {
        hud.update(round.score, record)
        sounds.blip(effect ?? food.kind)
      }
    }

    // After a stalled frame, carry on rather than race to catch up.
    backlog = Math.min(backlog, intervalOf(round))
    // The hot waves and the pineapple's blink move between steps too. With
    // reduced motion neither does, so only a step or advance() changes the picture.
    const animated = !prefersReducedMotion() && (hasEffect(round, 'hot') || pineappleFading(round))
    if (steps || changed || animated) draw()
  }

  function start(directionName = 'up') {
    if (mode === 'loading') {
      pendingStart = directionName
      return
    }
    if (mode === 'paused') {
      resume()
      return
    }
    if (mode !== 'idle' && mode !== 'over') return

    clearTimeout(deathTimer)
    round = null
    relayout()
    if (!grid) return

    round = createRound(grid, wordCells, directionName)
    result = null
    mode = 'playing'
    backlog = 0
    hud.update(0, record)
    loadRecord()
    setState({ gamePlaying: true })
    draw()
    run()
  }

  function pause() {
    if (mode !== 'playing') return
    mode = 'paused'
    cancelAnimationFrame(frame)
    if (pauseLabel) pauseLabel.hidden = false
    // Redrawn, so the frozen frame shows a blinking pineapple faded, not gone (see draw()).
    draw()
  }

  function resume() {
    if (mode !== 'paused') return
    mode = 'playing'
    if (pauseLabel) pauseLabel.hidden = true
    backlog = 0
    run()
  }

  function toIdle() {
    clearTimeout(deathTimer)
    cancelAnimationFrame(frame)
    mode = 'idle'
    round = null
    result = null
    dialogWaiting = false
    if (pauseLabel) pauseLabel.hidden = true
    hud.hide()
    setState({ gamePlaying: false })
    relayout()
  }

  function die() {
    cancelAnimationFrame(frame)
    mode = 'over'
    result = { score: round.score, duration: Math.round(round.time) }
    setState({ gamePlaying: false })
    sounds.crash()
    draw()

    if (prefersReducedMotion()) {
      deathTimer = window.setTimeout(openDialog, 400)
      return
    }

    let blinks = 0
    const blink = () => {
      blinks += 1
      draw({ hideSnake: blinks % 2 === 1 })
      deathTimer =
        blinks < DEATH_BLINKS
          ? window.setTimeout(blink, DEATH_BLINK_MS)
          : window.setTimeout(openDialog, DEATH_BLINK_MS * 2)
    }
    deathTimer = window.setTimeout(blink, DEATH_BLINK_MS)
  }

  function openDialog() {
    if (!dialog || !result) {
      toIdle()
      return
    }
    if (dialog.open) return
    // Opened the nav during the death blink: wait until it is closed again.
    dialogWaiting = navOpen()
    if (dialogWaiting) return

    submitting = false
    submitted = false
    if (finalScore) finalScore.textContent = formatScore(result.score)
    // Nothing to enter for zero points; the list is still worth a look.
    setEntering(result.score > 0)
    if (nameInput) nameInput.value = storage.get(NAME_STORAGE_KEY) ?? ''
    if (submitButton) submitButton.disabled = false
    setStatus()
    if (boardList) renderMessage(boardList, strings.loading ?? '…')

    dialog.returnValue = ''
    dialog.showModal()
    // From the top, whatever the last round left (the body scrolls, or on a
    // phone held sideways its columns).
    for (const box of dialogBody ? [dialogBody, ...dialogBody.children] : []) box.scrollTop = 0
    // showModal() focuses the first focusable element — on a short sheet that
    // is the overflowing body itself. The field it is, or "nochmal".
    const first = result.score > 0 ? nameInput : againButton
    first?.focus({ preventScroll: true })
    // Sized before the first paint, and the field brought into view.
    fitDialog()
    loadBoard()
  }

  function loadBoard() {
    if (!listUrl || !boardList) return
    fetchBoard(listUrl, signal)
      .then(({ best, top }) => {
        record = best?.score ?? 0
        if (result) hud.update(result.score, record)
        // A submission may have answered first, with the player's own rank in it.
        if (!submitted) renderBoard(boardList, top, { emptyText: strings.empty })
      })
      .catch((error) => {
        if (error.name === 'AbortError' || submitted) return
        renderMessage(boardList, strings.loadError ?? '')
      })
  }

  async function submit(event) {
    event.preventDefault()
    if (!result || submitting || submitted || !submitUrl) return

    submitting = true
    if (submitButton) submitButton.disabled = true
    setStatus(strings.saving)
    revealField()
    const name = nameInput?.value.trim() ?? ''
    storage.set(NAME_STORAGE_KEY, name)

    try {
      const response = await submitScore(
        submitUrl,
        { name, score: result.score, duration: result.duration },
        signal
      )

      if (response.status !== 201) {
        setStatus(response.status === 429 ? strings.rateLimited : strings.saveError)
        revealProblem()
        return
      }

      submitted = true
      setEntering(false)
      record = response.best?.score ?? record
      hud.update(result.score, record)
      setStatus((strings.saved ?? '%d').replace('%d', String(response.rank ?? '')))
      if (boardList) {
        renderBoard(boardList, response.top, { entry: response.entry, emptyText: strings.empty })
        // The player's own row, highlighted — also when it is below the top 10
        // — and, where it fits above it, the line with the rank.
        reveal(
          [boardList.querySelector('[aria-current]'), statusLine],
          prefersReducedMotion() ? 'auto' : 'smooth'
        )
      }
      againButton?.focus()
    } catch (error) {
      if (error.name !== 'AbortError') {
        setStatus(strings.saveError)
        revealProblem()
      }
    } finally {
      submitting = false
      if (!submitted && submitButton) submitButton.disabled = false
    }
  }

  // Keyboard: arrows/WASD steer (and start a round when idle), space/P pause.
  listen(window, 'keydown', (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
    if (dialog?.open || isEditable(event.target)) return

    // Behind the nav overlay the keys belong to the nav.
    if (navOpen()) return

    const direction = KEY_DIRECTIONS[event.code] ?? KEY_DIRECTIONS[event.key]
    if (direction) {
      if (mode === 'over') return
      event.preventDefault()
      sounds.unlock()
      if (mode === 'playing' || mode === 'paused') {
        resume()
        turn(round, direction)
      } else {
        start(direction)
      }
      return
    }

    if (PAUSE_KEYS.has(event.code) && (mode === 'playing' || mode === 'paused')) {
      event.preventDefault()
      if (mode === 'playing') pause()
      else resume()
    }
  })

  /** The centre of the snake's head on screen, in client px. */
  function headOnScreen() {
    const head = round?.body[0]
    if (!head || !grid) return null
    const box = renderer.canvas.getBoundingClientRect()
    return {
      x: box.left + grid.offsetX + (head.x + 0.5) * grid.cell,
      y: box.top + grid.offsetY + (head.y + 0.5) * grid.cell,
    }
  }

  /**
   * The side of the head a tap is on along one axis: -1 or 1, or 0 within
   * half a cell of the head's line, where the side is unclear.
   *
   * Close to an edge of the area taps can reach — the screen sideways, the
   * part between the bars up and down — there is no room left to tap beyond
   * the head. There a tap more than half the board away counts the short way
   * round, through that edge, as the snake wraps: with the head along the
   * left edge, a tap near the right edge turns left.
   * @param {{from: number, to: number}} reach  where taps land on this axis
   * @param {number} period  the board's size on this axis, in px
   */
  function tapSide(tap, head, reach, period) {
    const { cell } = grid
    const edge = cell / 2 + TAP_EDGE_CELLS * cell
    const nearEdge = head - reach.from < edge || reach.to - head < edge
    let distance = tap - head
    if (nearEdge && Math.abs(distance) > period / 2) distance -= Math.sign(distance) * period
    return Math.abs(distance) > cell / 2 ? Math.sign(distance) : 0
  }

  /**
   * Turn towards a tap beside the head: up or down while the snake runs
   * sideways, left or right while it runs up or down — always a quarter turn,
   * never a reversal. The heading is the last turn queued, so two quick taps
   * make a U-turn like two key presses.
   * @returns {boolean}  the turn was queued
   */
  function tapTurn(x, y) {
    const head = headOnScreen()
    if (!head) return false
    const heading = round.queue.at(-1) ?? round.direction
    const box = renderer.canvas.getBoundingClientRect()

    if (heading.x !== 0) {
      const bars = safeArea?.getBoundingClientRect() ?? box
      const side = tapSide(y, head.y, { from: bars.top, to: bars.bottom }, grid.rows * grid.cell)
      return side !== 0 && turn(round, side < 0 ? 'up' : 'down')
    }
    const side = tapSide(x, head.x, { from: box.left, to: box.right }, grid.cols * grid.cell)
    return side !== 0 && turn(round, side < 0 ? 'left' : 'right')
  }

  /** A square flashing open where a tap turned the snake (not with reduced motion). */
  function markTap(x, y) {
    if (prefersReducedMotion()) return
    const mark = document.createElement('span')
    mark.className = 'snake-tap'
    mark.dataset.snakeTap = ''
    mark.setAttribute('aria-hidden', 'true')
    mark.style.left = `${x}px`
    mark.style.top = `${y}px`
    if (grid) mark.style.setProperty('--snake-tap-size', `${grid.cell * TAP_MARK_CELLS}px`)
    block.append(mark)
    // animationend removes it; the timer too, should the animation not run.
    const remove = () => mark.remove()
    mark.addEventListener('animationend', remove, { once: true })
    window.setTimeout(remove, TAP_MARK_MS)
  }

  // Touch and pen: a tap starts or resumes a round, and steers while it runs,
  // as does a swipe — every finger on its own. While a round runs the canvas
  // takes the touches (touch-action: none), so the page can't scroll. Only a
  // pointer that goes down on the block is game input — taps on the bars, the
  // chat or the dialog never are; from there on it is followed wherever it goes.
  listen(block, 'pointerdown', (event) => {
    if (event.pointerType === 'mouse' || dialog?.contains(event.target)) return
    touches.set(event.pointerId, {
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      time: event.timeStamp,
      swiped: false,
      // Only a tap that began in a running round steers; the one that starts
      // or resumes it doesn't.
      playing: mode === 'playing',
    })
  })

  listen(window, 'pointermove', (event) => {
    const touch = touches.get(event.pointerId)
    if (!touch || mode !== 'playing') return
    const dx = event.clientX - touch.x
    const dy = event.clientY - touch.y
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_PX) return

    const horizontal = Math.abs(dx) > Math.abs(dy)
    turn(round, horizontal ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up')
    // Re-anchor, so one continuous gesture can steer several times.
    touch.x = event.clientX
    touch.y = event.clientY
    touch.swiped = true
  })

  listen(window, 'pointerup', (event) => {
    const touch = touches.get(event.pointerId)
    if (!touch) return
    touches.delete(event.pointerId)
    const moved = Math.hypot(event.clientX - touch.startX, event.clientY - touch.startY)
    const tap = !touch.swiped && moved < TAP_PX && event.timeStamp - touch.time < TAP_MAX_MS
    sounds.unlock()
    if (!tap || navOpen()) return
    if (mode === 'idle') start()
    else if (mode === 'paused') resume()
    else if (mode === 'playing' && touch.playing && tapTurn(event.clientX, event.clientY)) {
      markTap(event.clientX, event.clientY)
    }
  })

  listen(window, 'pointercancel', (event) => touches.delete(event.pointerId))

  // Dialog
  listen(form, 'submit', submit)
  listen(againButton, 'click', () => {
    sounds.unlock()
    dialog.close('again')
  })
  listen(closeButton, 'click', () => dialog.close('close'))
  // Also reached with Escape, which leaves the empty return value.
  listen(dialog, 'close', () => {
    dialog.style.removeProperty('--snake-dialog-top')
    dialog.style.removeProperty('--snake-dialog-height')
    if (dialog.returnValue === 'again') start()
    else toIdle()
  })
  // The on-screen keyboard shrinks the visual viewport (fitDialog() then keeps
  // the field in sight); a hardware keyboard doesn't, so the focus reveals it too.
  listen(window.visualViewport, 'resize', fitDialog)
  listen(window.visualViewport, 'scroll', fitDialog)
  listen(nameInput, 'focus', revealField)

  // Pause whenever the player can't be watching.
  listen(document, 'visibilitychange', () => {
    if (document.hidden) pause()
  })
  listen(window, 'blur', () => pause())
  listen(window, 'resize', scheduleRelayout)

  const observer = new ResizeObserver(scheduleRelayout)
  observer.observe(renderer.canvas)

  // Moving the window to a screen with another pixel ratio changes no CSS
  // size, so neither of the above fires; the backing store still has to
  // follow, or the pixel art is upscaled and blurred. The query matches the
  // current ratio only, so it is armed afresh for every new one.
  const watchPixelRatio = () => {
    const query = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
    listen(
      query,
      'change',
      () => {
        watchPixelRatio()
        scheduleRelayout()
      },
      { once: true }
    )
  }
  watchPixelRatio()

  const offPlay = on('play', () => {
    sounds.unlock()
    if (!navOpen()) start()
  })

  const offState = subscribe((state, changed) => {
    if (changed.includes('soundOn') && state.soundOn) sounds.unlock()
    if (changed.includes('chatOpen') && state.chatOpen) pause()
    if (changed.includes('navOpen')) {
      // The round stays paused after the nav closes; a key or tap resumes it.
      if (state.navOpen) {
        pause()
      } else if (dialogWaiting) {
        // A nav link closes the nav and starts a page visit, which changes the
        // URL within the same task: only bring the dialog back when we stay.
        const href = window.location.href
        clearTimeout(reopenTimer)
        reopenTimer = window.setTimeout(() => {
          if (!destroyed && dialogWaiting && window.location.href === href) openDialog()
        })
      }
    }
  })

  rasterizeWord(word).then((rasterized) => {
    if (destroyed) return
    shape = rasterized
    mode = 'idle'
    relayout()
    // Swap the text version for the squares (it stays if the font gave none).
    block.classList.toggle('is-ready', shape.cells.length > 0)
    if (pendingStart && !navOpen()) start(pendingStart)
  })

  return () => {
    destroyed = true
    controller.abort()
    offPlay()
    offState()
    observer.disconnect()
    cancelAnimationFrame(frame)
    cancelAnimationFrame(resizeFrame)
    clearTimeout(deathTimer)
    clearTimeout(reopenTimer)
    if (dialog?.open) dialog.close()
    renderer.destroy()
    sounds.close()
    hud.hide()
    if (pauseLabel) pauseLabel.hidden = true
    block.querySelectorAll('[data-snake-tap]').forEach((mark) => mark.remove())
    block.classList.remove('is-ready')
    setState({ gamePlaying: false })
  }
}

/** Easy view: no game, just the top list under the text version. */
function mountStatic(block) {
  const board = block.querySelector('[data-snake-static-board]')
  const list = board?.querySelector('[data-snake-board-list]')
  const listUrl = block.dataset.snakeListUrl
  if (!board || !list || !listUrl) return undefined

  const controller = new AbortController()
  fetchBoard(listUrl, controller.signal)
    .then(({ top }) => {
      if (!top.length) return
      renderBoard(list, top)
      board.hidden = false
    })
    .catch(() => {})

  return () => {
    controller.abort()
    board.hidden = true
    list.replaceChildren()
  }
}

onPage((root) => {
  const block = root.querySelector('[data-snake]')
  if (!block) return undefined
  return getState().easyMode ? mountStatic(block) : mountGame(block)
})
